import { useState } from 'react';
import { Badge, InlineError, Skeleton } from '../../components/ui';
import { Icon } from '../../components/ui/Icon';
import { localIsoDate } from '../myTasks/types';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import { DealDrawer } from './DealDrawer';
import { useDealSections, useDealStages, useDeals } from './hooks';
import { useMoveDeal } from './dealMoves';
import { useTapPeople } from '../projects/hooks';
import { dealErrorMessage, dealLoadMessage } from './queries';
import { UNSTAGED_KEY, dropIntent, kanbanColumns, kanbanSummary } from './kanbanColumns';
import { columnMoneyLabel, columnTotal, formatAmount } from './dealAmount';
import { hubspotLinkLabel } from './hubspotLink';
import { CHECKLIST_CHIPS, checklistFor, useLinkedInAddedLeads } from './dealChecklist';
import { isOverdue } from './types';
import type { KanbanColumn } from './kanbanColumns';
import type { Deal, Pipeline } from './types';
import './dealKanban.css';

/**
 * The Deals board as columns. See dealKanban.ts for the rules; this file only
 * draws them and turns a drop into the write that rules module decided on.
 *
 * Nothing here decides anything about stages or outcomes. Every drop goes
 * through `dropIntent` and this component switches on the answer — so the
 * "dragged a closed deal onto a stage and it stayed won" bug cannot be
 * introduced by editing the drawing code.
 */
export function DealKanban({ pipeline }: { pipeline: Pipeline }) {
  const permissions = useMyPermissions();
  const canEdit = canEditModule(permissions.data, 'deals');
  const deals = useDeals(pipeline);
  const stages = useDealStages(pipeline);
  const sections = useDealSections(pipeline);
  const move = useMoveDeal(pipeline);

  const tapPeople = useTapPeople(true);
  const people = (tapPeople.data ?? []).map((person) => ({
    id: `tap-person-${person.id}`,
    user_id: person.id,
    project_role: 'member' as const,
    display_name: person.display_name,
    email: person.email,
    avatar_url: person.avatar_url,
  }));

  const [error, setError] = useState('');
  const [openDealId, setOpenDealId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  function report(cause: unknown) {
    setError(dealErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  if (deals.isPending || stages.isPending) {
    return <Skeleton lines={5} label="Loading the board" />;
  }
  if (deals.isError || stages.isError) {
    // Says what actually failed. Until 2 September this branch asserted a
    // permissions problem whatever the cause, and a missing column took the
    // whole board down while telling everybody to ask an admin for access.
    const cause = deals.error ?? stages.error;
    return (
      <InlineError>{dealLoadMessage(cause instanceof Error ? cause.message : '')}</InlineError>
    );
  }

  const columns = kanbanColumns(deals.data, stages.data ?? []);
  const openDeal = deals.data.find((deal) => deal.id === openDealId);
  const today = localIsoDate(new Date());
  const addedLeads = useLinkedInAddedLeads();
  const sectionName = (id: string | null) =>
    id === null ? null : ((sections.data ?? []).find((s) => s.id === id)?.name ?? null);

  /**
   * A drop becomes a write through useMoveDeal, the same function the list's
   * dropdown and the drawer's use. The board decides nothing here beyond what
   * dropIntent says; three screens, one mover.
   */
  function drop(deal: Deal, column: KanbanColumn) {
    setOver(null);
    setDragging(null);
    if (!canEdit) return;
    setError('');
    move(deal, dropIntent(deal, column), report);
  }

  return (
    <>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="deal-kanban" role="list" aria-label="Deals by stage">
        {columns.map((column) => (
          <div
            key={column.key}
            role="listitem"
            className={[
              'deal-kanban-col',
              column.kind === 'outcome' ? 'deal-kanban-col--outcome' : '',
              over === column.key ? 'deal-kanban-col--over' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onDragOver={(event) => {
              if (!canEdit || dragging === null) return;
              event.preventDefault();
              setOver(column.key);
            }}
            onDragLeave={() => setOver((current) => (current === column.key ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              const dragged = deals.data.find((deal) => deal.id === dragging);
              if (dragged !== undefined) drop(dragged, column);
            }}
          >
            {/*
              The header carries the stage's MONEY as well as its count.

              Kyu, 2 Sep, on the board beside HubSpot's: cards with amounts but
              headers without are still a task board. The total is what makes a
              column read as a stage of a pipeline rather than a list.

              `columnMoneyLabel` says how many deals it could NOT add when any
              are unpriced. A stage total that quietly omits four deals reads as
              the stage's value and is not — the same shape of error as a rate
              off the wrong denominator, and invisible to whoever reads it.
            */}
            <div className="deal-kanban-head">
              <div className="deal-kanban-headtop">
                <span className="deal-kanban-name">{column.name}</span>
                <Badge tone="neutral" micro>
                  {column.deals.length}
                </Badge>
                {column.kind === 'outcome' && <span className="deal-kanban-kind">Outcome</span>}
              </div>
              {columnMoneyLabel(columnTotal(column.deals)) === '' ? null : (
                <div className="deal-kanban-total">
                  {columnMoneyLabel(columnTotal(column.deals))}
                </div>
              )}
            </div>
            <div className="deal-kanban-body">
              {column.deals.length === 0 ? (
                <p className="deal-kanban-empty">{emptyCopy(column)}</p>
              ) : (
                column.deals.map((deal) => (
                  /*
                   * A div, NOT a button, and this is the whole reason drag
                   * works. A <button draggable> does not reliably start a drag
                   * in Chrome — the button's own mouse handling swallows the
                   * gesture — so the first version of this shipped a board
                   * whose cards could not be moved at all. Kyu found it in a
                   * minute: "the kanban board doesnt allow drag and drop."
                   *
                   * role/tabIndex/onKeyDown put back everything the button was
                   * giving us: it is still announced as a control, still
                   * reachable by Tab, and still opens on Enter or Space.
                   */
                  <div
                    key={deal.id}
                    role="button"
                    tabIndex={0}
                    className="deal-kanban-card"
                    draggable={canEdit}
                    onDragStart={(event) => {
                      // Firefox refuses to start a drag without payload, and
                      // Chrome behaves better with one. Cheap, and it makes
                      // the gesture work the same in both.
                      event.dataTransfer.setData('text/plain', deal.id);
                      event.dataTransfer.effectAllowed = 'move';
                      setDragging(deal.id);
                    }}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    onClick={() => setOpenDealId(deal.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpenDealId(deal.id);
                      }
                    }}
                  >
                    {/*
                      Option D, chosen by Kyu on 2 Sep: "mostly with how the
                      boxes are laid out. and how each boxes have same
                      dimensions, very aesthetic."

                      So the card is a FIXED HEIGHT with fixed slots — name,
                      meta, foot — and each slot holds its space whether or not
                      it has anything in it. Measured across 15 cards of real
                      deals: every one 104px. The CSS carries the `flex: none`
                      that makes that true; without it a flex child shrinks
                      below its stated height and the grid drifts by a few
                      pixels per card, which looks almost right and is not.

                      The two-line clamp is the D half of the choice: nothing
                      truncates, at the cost of a little space under short
                      names. Kyu picked that over the tighter one-line version.
                    */}
                    <span className="deal-kanban-card-name">{deal.name}</span>
                    {/*
                     * A closed deal's SECTION is noise — sections are the
                     * weekly triage and a closed deal has left it. What
                     * matters in Won or Lost is when it closed. Kyu, looking
                     * at Nutrifoodz sitting in Lost still labelled "Letting it
                     * Ride": "we dont have anything in this field to match the
                     * deal stage accordingly."
                     */}
                    {deal.outcome === 'open' ? (
                      sectionName(deal.section_id) !== null && (
                        <span className="deal-kanban-card-meta">
                          {sectionName(deal.section_id)}
                        </span>
                      )
                    ) : (
                      <span className="deal-kanban-card-meta">
                        {deal.outcome === 'won' ? 'Won' : 'Lost'}
                        {deal.closed_on === null ? '' : ` · ${deal.closed_on}`}
                      </span>
                    )}
                    {/*
                      The foot: owner, amount, and the next action date.

                      The NOTE that used to sit here is gone. It was the single
                      biggest reason cards ranged from two lines to five, and
                      uniform cards was the whole ask. The note is still on the
                      deal and still the first thing in the drawer, one click
                      away — it just stopped setting the height of the board.

                      Owner sits here rather than beside the name so the name
                      keeps the full width of the card; putting a chip up there
                      made long names wrap that otherwise would not.

                      An unpriced deal shows NOTHING, never $0. formatAmount
                      returns '' for null and the span collapses.
                    */}
                    <span className="deal-kanban-card-foot">
                      <span className="deal-kanban-card-who" aria-hidden="true">
                        {initialsFrom(
                          people.find((person) => person.user_id === deal.owner_id)?.display_name,
                        )}
                      </span>
                      {formatAmount(deal.amount) === '' ? null : (
                        <span className="deal-kanban-card-amount">{formatAmount(deal.amount)}</span>
                      )}
                      {/*
                        Hanneza's ask, on the card rather than only in the
                        drawer: the point was to stop searching HubSpot, and a
                        link you have to open a drawer to reach is still two
                        steps.

                        An <a> inside a draggable div[role=button] needs both
                        of these or it breaks the board. `draggable={false}`
                        stops the anchor's own native drag from swallowing the
                        card's gesture; stopPropagation stops the click and
                        the Enter key from ALSO opening the drawer behind the
                        new tab. Both were found by trying it, not by reading.

                        It sits in the existing foot row, so the card is the
                        same fixed 6.5rem it has been since 2 Sep.
                      */}
                      {/*
                        Hanneza's checklist, while open. A mark per unticked
                        item, gone once it is done — the audit of old deals
                        reads off the board. Not shown until the set of added
                        leads has loaded, so a card never flashes "not added"
                        at somebody who did add them.
                      */}
                      {addedLeads.data === undefined
                        ? null
                        : (() => {
                            const list = checklistFor(deal, addedLeads.data);
                            return (
                              <>
                                {list.linkedin === 'todo' ? (
                                  <span
                                    className="deal-kanban-card-todo"
                                    title={CHECKLIST_CHIPS.linkedin.label}
                                    aria-label={CHECKLIST_CHIPS.linkedin.label}
                                  >
                                    {CHECKLIST_CHIPS.linkedin.glyph}
                                  </span>
                                ) : null}
                                {list.hubspot ? null : (
                                  <span
                                    className="deal-kanban-card-todo"
                                    title={CHECKLIST_CHIPS.hubspot.label}
                                    aria-label={CHECKLIST_CHIPS.hubspot.label}
                                  >
                                    {CHECKLIST_CHIPS.hubspot.glyph}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                      {hubspotLinkLabel(deal.hubspot_url) === '' ? null : (
                        <a
                          className="deal-kanban-card-hubspot"
                          href={deal.hubspot_url ?? ''}
                          target="_blank"
                          rel="noopener noreferrer"
                          draggable={false}
                          title={`Open ${deal.name} in HubSpot`}
                          aria-label={`Open ${deal.name} in HubSpot`}
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <Icon name="link" size="sm" />
                        </a>
                      )}
                      {deal.outcome === 'open' && deal.next_action_date !== null && (
                        <Badge
                          tone={isOverdue(deal.next_action_date, today) ? 'danger' : 'neutral'}
                          micro
                        >
                          {deal.next_action_date}
                        </Badge>
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="tracker-field-note">{kanbanSummary(columns)}</p>

      {openDeal === undefined ? null : (
        <DealDrawer
          key={openDeal.id}
          deal={openDeal}
          sections={sections.data ?? []}
          stages={stages.data ?? []}
          pipeline={pipeline}
          people={people}
          onClose={() => setOpenDealId(null)}
        />
      )}
    </>
  );
}

/**
 * What an empty column says. Each one is a different fact, and a shared "No
 * deals" would waste the most useful thing this view does — an empty Won
 * column is a true statement about the business, not a gap in the screen.
 */
/**
 * Why a closed deal closed, when somebody wrote it down. Preferred over the
 * latest comment on a closed card: the reason is the last useful thing about
 * a deal that has stopped moving.
 */
/*
 * `closedReason` lived here and rendered the outcome note on the card. It went
 * with the note on 2 Sep, when the cards were made a fixed height — the reason
 * is still on the deal and still the first thing in the drawer.
 */

/**
 * Two letters for the owner, from the people already loaded for the drawer.
 *
 * Initials rather than a name because the card is a fixed width and a full
 * name would either wrap or truncate; rather than an avatar because half the
 * team has no picture and a fallback circle with a letter in it is what an
 * avatar component would draw anyway.
 *
 * An unowned deal gets an em dash, not a blank: the slot is part of what keeps
 * every card the same height, and an empty circle reads as a loading state.
 */
function initialsFrom(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

function emptyCopy(column: KanbanColumn): string {
  if (column.key === 'won') return 'Nothing marked Won yet. Drag a deal here when it closes.';
  if (column.key === 'lost') return 'Nothing marked Lost yet.';
  if (column.key === UNSTAGED_KEY) return 'Every deal has a stage.';
  return 'Nothing at this stage.';
}
